import { useState } from 'react';
import type { ReactNode } from 'react';
import { ConsoleProvider, useConsole, useOperator } from './context';
import type { Console } from './context';
import { SignIn } from './SignIn';
import { Fleet } from './Fleet';
import { TenantDetail } from './TenantDetail';
import { Audit } from './Audit';
import { Jobs } from './Jobs';
import { Wizard } from './Wizard';
import { actorOf } from '../auth/fake';

/**
 * The shell: three tabs and a stack of one.
 *
 * `apps/app` has six tabs because a counter has six jobs. This seat has three
 * — **Fleet**, **Audit**, **Jobs** — and they are the three nouns spec §6.3
 * lists, minus the ones that are properties of a tenant rather than screens of
 * their own. Keys, webhooks, deliveries and lifecycle all live on the tenant
 * page, because every one of them is a question about *that business*, and a
 * "Keys" tab would have collected fourteen tenants' credentials under one word.
 *
 * No router, for `apps/app`'s reason one notch stronger: there are no deep
 * links to want, and a URL that survived a paste would be a cross-tenant screen
 * in somebody's chat history.
 *
 * The header states two things at all times: which mode the credential is in
 * (a fake token is never allowed to look like a real one), and the
 * `audit_log.actor` this session writes. The second is not decoration — every
 * request from this console, reads included, becomes a row under that name.
 */

export type Tab = 'fleet' | 'audit' | 'jobs';

export type View =
  | { kind: 'fleet' }
  | { kind: 'tenant'; tenantId: string }
  | { kind: 'wizard' }
  | { kind: 'audit'; tenantId?: string | undefined }
  | { kind: 'jobs'; tenantId?: string | undefined };

export function App({ console: value }: { console: Console }): ReactNode {
  return (
    <ConsoleProvider console={value}>
      <Authenticated />
    </ConsoleProvider>
  );
}

function Authenticated(): ReactNode {
  const { auth } = useConsole();
  const user = useOperator();
  if (user === null) return <SignIn auth={auth} />;
  return <Shell />;
}

function tabOf(view: View): Tab {
  if (view.kind === 'audit') return 'audit';
  if (view.kind === 'jobs') return 'jobs';
  return 'fleet';
}

function Shell(): ReactNode {
  const { auth } = useConsole();
  const user = useOperator();
  const [view, setView] = useState<View>({ kind: 'fleet' });
  const tab = tabOf(view);

  return (
    <div className="console">
      <header className="topbar">
        <div className="topbar__brand">
          <strong>yourstore</strong> <span className="topbar__seat">operator console</span>
        </div>

        <nav className="topbar__tabs" aria-label="Sections">
          <TabButton
            current={tab}
            id="fleet"
            label="Fleet"
            onPick={() => setView({ kind: 'fleet' })}
          />
          <TabButton
            current={tab}
            id="audit"
            label="Audit"
            onPick={() => setView({ kind: 'audit' })}
          />
          <TabButton
            current={tab}
            id="jobs"
            label="Jobs"
            onPick={() => setView({ kind: 'jobs' })}
          />
        </nav>

        <div className="topbar__who">
          {auth.kind === 'fake' ? (
            <span className="topbar__dev" title="Unsigned development token">
              dev token
            </span>
          ) : null}
          <span className="topbar__actor">{user === null ? '' : actorOf(user)}</span>
          <button
            type="button"
            className="button button--quiet"
            onClick={() => void auth.signOut()}
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="console__body">
        {view.kind === 'fleet' ? (
          <Fleet
            onOpen={(tenantId) => setView({ kind: 'tenant', tenantId })}
            onCreate={() => setView({ kind: 'wizard' })}
          />
        ) : null}

        {view.kind === 'tenant' ? (
          <TenantDetail
            tenantId={view.tenantId}
            onBack={() => setView({ kind: 'fleet' })}
            onAudit={(tenantId) => setView({ kind: 'audit', tenantId })}
            onJobs={(tenantId) => setView({ kind: 'jobs', tenantId })}
          />
        ) : null}

        {view.kind === 'wizard' ? (
          <Wizard
            onDone={(tenantId) => setView({ kind: 'tenant', tenantId })}
            onCancel={() => setView({ kind: 'fleet' })}
          />
        ) : null}

        {view.kind === 'audit' ? <Audit tenantId={view.tenantId} /> : null}

        {view.kind === 'jobs' ? <Jobs tenantId={view.tenantId} /> : null}
      </main>

      <footer className="console__foot">
        Every request from this console — reads included — writes an <code>audit_log</code> row
        (spec §6.3).
      </footer>
    </div>
  );
}

function TabButton({
  current,
  id,
  label,
  onPick,
}: {
  current: Tab;
  id: Tab;
  label: string;
  onPick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={`topbar__tab${current === id ? ' is-current' : ''}`}
      aria-current={current === id ? 'page' : undefined}
      onClick={onPick}
    >
      {label}
    </button>
  );
}
